import React, { useCallback, useContext, useEffect, useRef } from "react";
import MapboxDraw, { DrawCreateEvent, DrawModeChangeEvent, MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";
// @ts-expect-error - @turf/helpers types not properly resolved
import { featureCollection } from "@turf/helpers";
import { Feature, FeatureCollection, GeoJsonProperties, Geometry } from "geojson";
import { defer, isEmpty, isEqual } from "lodash";
import mapboxgl, { FeatureIdentifier } from "mapbox-gl";

import { DRAWING_CONSTANTS } from "fond/constants";
import defaultStyles from "fond/draw/defaultStyles";
import { HistoryContext, useHistory } from "fond/history";
import { useDisableContextMenu } from "fond/hooks/useDisableContextMenu";
import { MapContext } from "fond/map/MapProvider";
import { defaultToolbarConfig, ToolbarConfig } from "fond/map/Toolbar";
import { MapboxStyleLayer } from "fond/types";

import AttributeFormPopup, { AttributeFormPopupHandle } from "./AttributeFormPopup";
import { getDrawModes, snappingOptions, updateFeatureCollection } from "./helper";

export interface DrawHandlerProps {
  modes?: {
    [modeKey: string]: MapboxDraw.DrawCustomMode<any, any>;
  };
  /**
   * The configuration of toolbar actions presented to the user.
   * @default defaultToolbarConfig
   */
  config?: ToolbarConfig;
  /**
   * The initial features that should be loaded into the drawing tool.
   */
  initialFeatures?: Feature[] | null;
  /**
   * The original mapbox sources layers that contain the features to be edited / drawn.
   * This is used to determine which existing features on the map should be hidden during drawing.
   */
  sources?: FeatureIdentifier[];
  /**
   * The options to pass to the MapboxDraw instance.
   */
  options?: MapboxDrawOptions;
  /**
   * Callback function that used within the onCreate feature function to provide feature properties.
   */
  getFeatureProperties?(feature: Feature, editingLayerId?: string): GeoJsonProperties;
  /**
   * The default starting mode to set on load.
   * @default "simple_select"
   */
  startingMode?:
    | "simple_select"
    | "draw_line_string"
    | "draw_polygon"
    | "draw_point"
    | "static"
    | "passing_draw_polygon"
    | "passing_draw_line_string"
    // Allow custom mode names while keeping the built-ins as hints.
    | (string & {});
  /**
   * Mapbox-gl-draw styles to be added to the map.
   */
  styles?: MapboxStyleLayer[];
  /**
   * Whether to disable hover effects on features.
   * Hover handling forces a redraw of any feature that starts/stops being hovered, which can cause flickering.
   *
   * @default false
   */
  disableHoverEffects?: boolean;
}

const DrawHandler: React.FC<DrawHandlerProps> = ({
  config = defaultToolbarConfig,
  modes,
  startingMode = "simple_select",
  initialFeatures,
  options,
  sources,
  styles,
  getFeatureProperties,
  disableHoverEffects,
}: DrawHandlerProps) => {
  const { state: historyState, eventType, set: setHistory, clear: clearHistory } = useHistory<FeatureCollection>(HistoryContext);
  const { map, drawControl, setIsDirty, setDrawMode, cleanUpDrawControl, toolbar } = useContext(MapContext);

  // Disable browser context menu during drawing operations
  useDisableContextMenu();

  const hoverFeatures = useRef(new Set<string>());
  const attributeFormPopupRef = useRef<AttributeFormPopupHandle>(null);

  // Initialize draw control
  useEffect(() => {
    // Cancel any active drawing mode (ruler, comments, etc.) and force-remove the existing
    // draw control before creating a fresh instance with custom modes/styles for this draw session.
    cleanUpDrawControl(true);

    drawControl.current = new MapboxDraw({
      // We turn on the built in controls to allow the map to capture keyboard events (required by MapboxDraw).
      // However we hide these controls via css as we diplay our own custom controls.
      displayControlsDefault: true,
      controls: {},
      userProperties: true,
      modes: getDrawModes(modes),
      ...snappingOptions,
      ...options,
      styles: styles || defaultStyles,
      clickBuffer: 5,
    });

    // Store base options for layer-specific option merging
    // @ts-expect-error - Adding custom property to store base options
    drawControl.current.baseOptions = { ...drawControl.current.options };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update draw control options dynamically (but preserve manually-set values like editingLayerId and snap)
  useEffect(() => {
    if (!drawControl.current) return;

    // Dynamically update the draw control options if they change - this allows for features like snapping to be toggled without needing to re-initialize the draw control (which would reset the drawn features).
    // Preserve options that may have been mutated directly (like editingLayerId from LayerSelection, or snap from SnappingButton)
    const currentEditOptions = drawControl.current.options?.editOptions;
    const currentSnap = drawControl.current.options?.snap;
    const currentTopologicalEditing = drawControl.current.options?.topologicalEditing;

    const newOptions = {
      ...drawControl.current.options,
      ...options,
      // Preserve the current snap/topological values if they were set manually (don't overwrite with stale props)
      snap: currentSnap !== undefined ? currentSnap : options?.snap,
      topologicalEditing: currentTopologicalEditing !== undefined ? currentTopologicalEditing : options?.topologicalEditing,
      // Preserve the current editOptions if they were set (don't overwrite with stale props)
      editOptions: {
        ...options?.editOptions,
        ...currentEditOptions,
      },
    };

    if (!isEqual(drawControl.current.options, newOptions)) {
      // Instead of replacing the options object (which breaks the reference to _ctx.options),
      // mutate the existing object in place to preserve the reference
      if (drawControl.current.options) {
        Object.keys(drawControl.current.options).forEach((key) => {
          if (!(key in newOptions)) {
            delete drawControl.current!.options![key as keyof typeof drawControl.current.options];
          }
        });
        Object.assign(drawControl.current.options, newOptions);
      }

      // Update base options when options prop changes (but preserve editOptions)
      // @ts-expect-error - Custom property
      drawControl.current.baseOptions = {
        ...newOptions,
        editOptions: {
          ...newOptions.editOptions,
          editingLayerId: undefined, // Don't store editingLayerId in base
        },
      };
    }
  }, [options, drawControl]);

  // Update toolbar config
  useEffect(() => {
    toolbar.current = config;
  }, [config, toolbar]);

  /**
   * Called when the history state needs updated
   */
  const updateHistory = useCallback(() => {
    if (drawControl.current?.getAll()) setHistory(drawControl.current?.getAll());
  }, [drawControl, setHistory]);

  /**
   * Called when validation completes to silently update history state
   * without creating a new history entry
   */
  const updateHistorySilent = useCallback(() => {
    if (drawControl.current?.getAll()) {
      setHistory(drawControl.current?.getAll(), { silent: true });
    }
  }, [drawControl, setHistory]);

  /**
   * Updates the existing features feature-state, allowing for
   * styles to change based on edit status.
   */
  const setExistingFeatureVisibility = useCallback(
    (visibile: boolean) => {
      if (sources?.length) {
        initialFeatures?.forEach(({ id }) => {
          sources.forEach(({ source: targetSource, sourceLayer: targetSourceLayer }) => {
            map?.setFeatureState({ id: id, source: targetSource, sourceLayer: targetSourceLayer }, { isEditing: !visibile });
          });
        });
      }
    },
    [sources, initialFeatures, map]
  );

  /**
   * On create if getFeatureProperties is provided (either at layer level or handler level),
   * we will update the feature properties with the properties returned from the function.
   */
  const handleOnCreate = useCallback(
    (event: DrawCreateEvent) => {
      // Read current values from drawControl to get the latest editingLayerId
      // (which may have been changed by LayerSelection)
      const currentEditingLayerId = drawControl.current?.options?.editOptions?.editingLayerId;
      const currentEditableLayers = drawControl.current?.options?.editOptions?.allLayers;
      const parentLayerId = drawControl.current?.options?.parentLayerId;

      // Find the layer-specific getFeatureProperties if available
      // For sublayers, use the parent layer's getFeatureProperties
      const layerIdToFind = parentLayerId || currentEditingLayerId;
      const currentLayer = currentEditableLayers?.find((layer) => layer.ID === layerIdToFind);
      const featurePropsGetter = currentLayer?.getFeatureProperties || getFeatureProperties;

      if (featurePropsGetter) {
        const newFeatures = event.features.map(
          (feature) => ({ ...feature, properties: featurePropsGetter(feature, currentEditingLayerId) }) as Feature<Geometry, GeoJsonProperties>
        );

        drawControl.current?.set(updateFeatureCollection(drawControl.current.getAll(), newFeatures));
      }
      updateHistory();

      // Show attribute form popup if the layer has it enabled
      if (event.features.length > 0) {
        const eventFeature = event.features[0]; // For now, handle single feature creation
        attributeFormPopupRef.current?.showForFeature(eventFeature.id as string);
      }
    },
    [drawControl, getFeatureProperties, updateHistory]
  );

  /**
   * When the maps style data changes (e.g. user changes to mono view)
   */
  const handleOnStyleChange = useCallback(() => {
    setExistingFeatureVisibility(false);
  }, [setExistingFeatureVisibility]);

  /**
   * Handle the initialization of drawing.
   * Adds the control to the map via simple_select, loads initial features,
   * then switches to the configured starting mode in a single effect.
   */
  useEffect(() => {
    // Add the control to the map in simple_select mode to initialize drawing tools
    setDrawMode("simple_select");

    if (initialFeatures) {
      drawControl.current?.set(featureCollection(initialFeatures));
    }

    // Initialize history with the starting state
    updateHistory();
    setExistingFeatureVisibility(false);

    // Switch to the configured starting mode after a deferred tick.
    // addControl is synchronous for the draw control's internal context, but
    // the map needs a frame to register the draw style layers. Without defer,
    // features created in the starting mode won't render visually.
    if (startingMode !== "simple_select" && drawControl.current && map?.hasControl(drawControl.current)) {
      defer(() => {
        if (drawControl.current && map?.hasControl(drawControl.current)) {
          drawControl.current.changeMode(startingMode as string);
          map.fire("draw.modechange", { type: "draw.modechange", mode: startingMode } as DrawModeChangeEvent);
        }
      });
    }
  }, [drawControl, initialFeatures, map, setDrawMode, setExistingFeatureVisibility, startingMode, updateHistory]);

  /**
   * Sync the drawing features with the current state of history when the user
   * clicks undo/redo.
   */
  useEffect(() => {
    // Only respond to undo/redo events
    if (eventType !== "undo" && eventType !== "redo") {
      return;
    }

    if (map && drawControl.current && map.hasControl(drawControl.current)) {
      // Track the current mode before clearing
      const currentMode = drawControl.current.getMode();

      // We need to clear the existing features before setting the new ones
      // to ensure re-rendering occurs correctly.
      drawControl.current?.set(featureCollection([]));
      if (isEmpty(historyState)) {
        drawControl.current.set(featureCollection(initialFeatures || []));
      } else {
        drawControl.current.set(historyState);
      }

      // set() clears the selection without emitting an event, so notify selection-dependent
      // UI (e.g. toolbar buttons) that the selection is now empty
      map.fire("draw.selectionchange", { type: "draw.selectionchange", features: drawControl.current.getSelected().features, points: [] });

      // If we were in a drawing mode, re-enter it to call onSetup and re-initialize the mode
      // We do this to re-calculate things like snapping after an undo/redo, which may change the geometry and require recalculating snap points
      if (currentMode === "draw_line_string" || currentMode === "draw_point" || currentMode === "draw_polygon") {
        defer(() => {
          if (drawControl.current && map?.hasControl(drawControl.current)) {
            drawControl.current.changeMode(currentMode as string, drawControl.current.options?.modeOptions);
            map.fire("draw.modechange", { type: "draw.modechange", mode: currentMode } as DrawModeChangeEvent);
          }
        });
      }
    }
  }, [eventType, historyState, initialFeatures, map, drawControl]);

  useEffect(() => {
    // Hide the existing features that we are about to edit
    setExistingFeatureVisibility(false);

    // Clean up when exiting drawing mode
    return () => {
      setExistingFeatureVisibility(true);
    };
  }, [drawControl, setExistingFeatureVisibility]);

  useEffect(() => {
    return () => {
      clearHistory();
      setDrawMode("no_feature");

      // Null out the ref so a fresh instance is created on remount with the new options
      drawControl.current = null;
    };
  }, [clearHistory, setDrawMode, drawControl, map]);

  const handleOnModification = useCallback(() => {
    setIsDirty(true);
  }, [setIsDirty]);

  /**
   * Handle draw options changed events (e.g., snap toggle from SnappingButton)
   * This allows any component to update draw options dynamically
   */
  const handleOptionsChanged = useCallback(
    (event: Partial<Pick<MapboxDrawOptions, "snap" | "topologicalEditing">>) => {
      if (drawControl.current?.options) {
        // Extract only the option properties we support, avoiding any circular references
        // that Mapbox might add to the event object (like map references)
        const cleanOptions: Partial<MapboxDrawOptions> = {};

        if ("snap" in event) {
          cleanOptions.snap = event.snap;
        }
        if ("topologicalEditing" in event) {
          cleanOptions.topologicalEditing = event.topologicalEditing;
        }

        // Merge the clean options into current options
        Object.assign(drawControl.current.options, cleanOptions);

        // Also update baseOptions so multi-layer editing doesn't reset them
        // @ts-expect-error - Custom property
        if (drawControl.current.baseOptions) {
          // @ts-expect-error - Custom property
          Object.assign(drawControl.current.baseOptions, cleanOptions);
        }
      }
    },
    [drawControl]
  );

  /**
   * Handle the mouse move event to show hover effects on features
   */
  const handleOnMouseMove = useCallback(
    (event: mapboxgl.MapMouseEvent) => {
      // Validate map still has the drawControl active
      if (!drawControl.current || !map?.hasControl(drawControl.current)) return;

      // Remove any existing hover effects
      hoverFeatures.current.forEach((id) => {
        if (id) {
          hoverFeatures.current.delete(id);
          if (!drawControl.current?.get(id)) return;
          drawControl.current.setFeatureProperty(id, DRAWING_CONSTANTS.Hover, "false");
          drawControl.current.add(drawControl.current.get(id) as Feature);
        }
      });

      // Add new hover effects
      drawControl.current?.getFeatureIdsAt(event.point).forEach((id) => {
        if (id) {
          hoverFeatures.current.add(id);
          if (!drawControl.current?.get(id)) return;
          drawControl.current.setFeatureProperty(id, DRAWING_CONSTANTS.Hover, "true");
          drawControl.current.add(drawControl.current.get(id) as Feature);
        }
      });
    },
    [drawControl, map]
  );

  // Bind events to track if the user has made any changes
  useEffect(() => {
    const dirtyEvents = ["draw.create", "draw.update", "draw.delete"];
    dirtyEvents.forEach((event) => {
      map?.on(event, handleOnModification);
    });

    return () => {
      dirtyEvents.forEach((event) => {
        map?.off(event, handleOnModification);
      });
    };
  }, [handleOnModification, map, setIsDirty]);

  useEffect(() => {
    const historyEvents = ["draw.update", "draw.combine", "draw.uncombine", "draw.delete", "draw.union"];
    historyEvents.forEach((event) => {
      map?.on(event, updateHistory);
    });

    map?.on("draw.create", handleOnCreate);
    map?.on("draw.validation", updateHistorySilent);
    map?.on("styledata", handleOnStyleChange);
    map?.on("draw.options_changed", handleOptionsChanged);

    // 'hover' handling forces a redraw of any feature that starts/stops being hovered; this can cause flickering.
    if (!disableHoverEffects) {
      map?.on("mousemove", handleOnMouseMove);
    }

    return () => {
      historyEvents.forEach((event) => {
        map?.off(event, updateHistory);
      });

      map?.off("draw.create", handleOnCreate);
      map?.off("draw.validation", updateHistorySilent);
      map?.off("styledata", handleOnStyleChange);
      map?.off("draw.options_changed", handleOptionsChanged);
      if (!disableHoverEffects) {
        map?.off("mousemove", handleOnMouseMove);
      }
    };
  }, [
    updateHistory,
    updateHistorySilent,
    handleOnCreate,
    handleOnMouseMove,
    handleOnStyleChange,
    handleOptionsChanged,
    map,
    setDrawMode,
    disableHoverEffects,
  ]);

  return <AttributeFormPopup ref={attributeFormPopupRef} />;
};

export default DrawHandler;
